// The feature-manifest contract: each feature declares its routes, breadcrumbs, sidebar entry
// and an enablement predicate. A dashboard app is just a shell that picks a list of manifests
// and derives its router + sidebar + breadcrumbs from them via buildRegistry() — one source of
// truth, so a future specific dashboard (operator, grower, …) is a different manifest subset.
import type { ComponentChildren, JSX } from 'preact';
import type { NavNode, NavSection } from '@xantory/ui';
import {
  CalendarIcon,
  HomeIcon,
  LayersIcon,
  LeafIcon,
  MonitorIcon,
  SettingsIcon,
  ShieldIcon,
  TruckIcon,
  WrenchIcon,
} from '@xantory/icons';

export type DeployMode = 'cloud' | 'local';

export type PermissionLevel = 'read' | 'write' | 'approve' | 'admin';

/** What feature enablement can depend on. */
export interface FeatureContext {
  mode: DeployMode;
  /** Backend `user_type` of the logged-in user — `'local'` | `'hq'` | `'admin'`;
   *  undefined pre-login. (It never was `'member'`: that is the UI's word for
   *  `'local'`, and `'hq'` arrived with R-348.) */
  userType: string | undefined;
  /**
   * RBAC check against the user's resolved permission set (R-058/D-061) —
   * UX gating only; the gateways enforce. Admins always pass.
   */
  can: (module: string, min: PermissionLevel) => boolean;
}

export interface FeatureRoute {
  path: string;
  /** Topbar breadcrumb: [section, page]; empty section = no separator. */
  breadcrumb: readonly [string, string];
  render: (query: URLSearchParams) => JSX.Element;
  /** Reachable without login (recovery/registration flows); rendered outside the shell. */
  public?: boolean;
}

export interface FeatureManifest {
  key: string;
  /** Absent ⇒ enabled for everyone. Disabled features drop out of routes AND nav. */
  enabled?: (ctx: FeatureContext) => boolean;
  routes: FeatureRoute[];
  /** Sidebar entry. Features sharing a `section` title are grouped, in manifest-list order. */
  nav?: { section: string; item: NavNode };
}

export interface Registry {
  /** Every enabled route path (public + private). */
  knownRoutes: string[];
  isPublic: (path: string) => boolean;
  breadcrumb: (path: string) => readonly [string, string];
  render: (path: string, query: URLSearchParams) => JSX.Element | null;
  navSections: NavSection[];
}

/** One glyph per sidebar section — all the collapsed rail shows (user-set 2026-09-30). Keyed by
 *  the ENGLISH `nav.section` title the manifests use, before the shell translates it. A section
 *  missing here still renders: the rail falls back to its first item's icon. */
const SECTION_ICONS: Record<string, ComponentChildren> = {
  HQ: <HomeIcon />,
  Monitor: <MonitorIcon />,
  Farm: <LeafIcon />,
  History: <CalendarIcon />,
  ITSM: <WrenchIcon />,
  Commerce: <TruckIcon />,
  Procurement: <LayersIcon />,
  Administration: <ShieldIcon />,
  Setup: <SettingsIcon />,
};

/** Resolve an app's manifest list against the current mode/user into router + sidebar data. */
export function buildRegistry(features: FeatureManifest[], ctx: FeatureContext): Registry {
  const active = features.filter((f) => !f.enabled || f.enabled(ctx));
  const routes = new Map(active.flatMap((f) => f.routes.map((r) => [r.path, r] as const)));

  const navSections: NavSection[] = [];
  for (const f of active) {
    if (!f.nav) continue;
    let sec = navSections.find((s) => s.title === f.nav!.section);
    if (!sec)
      navSections.push((sec = { title: f.nav.section, icon: SECTION_ICONS[f.nav.section], items: [] }));
    const item = f.nav.item;
    // Merge groups that share a `key`: features gated to different tiers can each
    // contribute children to one group (e.g. cloud-only Commons + both-tier
    // Locations under a single "Configuration" group). Every child stays gated by
    // its own feature's `enabled`, so the site tier shows only its children.
    // Clone the group + its children array on first insert so repeated
    // buildRegistry() calls never mutate the module-level manifest constants.
    if (item.kind === 'group') {
      const existing = sec.items.find((i) => i.kind === 'group' && i.key === item.key);
      if (existing && existing.kind === 'group') {
        existing.children = [...existing.children, ...item.children];
      } else {
        sec.items.push({ ...item, children: [...item.children] });
      }
      continue;
    }
    sec.items.push(item);
  }

  return {
    knownRoutes: [...routes.keys()],
    isPublic: (path) => routes.get(path)?.public === true,
    breadcrumb: (path) => routes.get(path)?.breadcrumb ?? ['', ''],
    render: (path, query) => routes.get(path)?.render(query) ?? null,
    navSections,
  };
}

/**
 * Programmatic navigation shared by features and shells. Uses the History API (clean paths, no `#`);
 * preact-iso's `LocationProvider` listens for `popstate`, so we push the new URL then dispatch a
 * synthetic `popstate` to make it re-read the location. (`<a href>` clicks are intercepted by
 * `LocationProvider` directly and don't come through here.)
 */
export function navigate(path: string, params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  history.pushState(null, '', path + qs);
  dispatchEvent(new PopStateEvent('popstate'));
}
